import React from "react";
import { Box, Typography, alpha } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TEMPERATURE_STATUS_COLORS } from "@ui/components/temperature";
import StatusIndicator from "@ui/components/status/StatusIndicator";
import { DesignTooltip } from "@ui/components/tooltip/DesignTooltip";
const ROW_MIN_HEIGHT = 88;
const ROW_RADIUS = "32px";
const CELL_PADDING_X = 2;
/**
 * `grid-template-columns` for a column set. `flex` columns start at their `minWidth` and share any
 * extra space by weight, so a row laid out for the design width stays exact there and spreads
 * evenly on wider screens. Columns without `flex` keep a fixed `width`.
 */
export const getDeviceDetailsTemplate = (columns) => columns
    .map((col) => {
    var _a, _b;
    return col.flex
        ? `minmax(${(_a = col.minWidth) !== null && _a !== void 0 ? _a : 0}px, ${col.flex}fr)`
        : `${(_b = col.width) !== null && _b !== void 0 ? _b : col.minWidth}px`;
})
    .join(" ");
/** Narrowest width at which the row still lays out as designed; below it the grid scrolls. */
export const getDeviceDetailsMinWidth = (columns) => columns.reduce((sum, col) => { var _a, _b; return sum + ((_b = (_a = col.minWidth) !== null && _a !== void 0 ? _a : col.width) !== null && _b !== void 0 ? _b : 0); }, 0);
const JUSTIFY = {
    left: "flex-start",
    center: "center",
    right: "flex-end",
};
/**
 * One row of the AP details table (Figma: DeviceDetailsRow).
 * Pure layout shell: the columns decide width and alignment, `renderCell` decides content.
 */
export const DeviceDetailsRow = ({ columns, variant = "default", renderCell, onClick, }) => {
    const highlight = variant === "highlight";
    return (React.createElement(Box, { onClick: onClick, sx: {
            position: "relative",
            cursor: onClick ? "pointer" : "default",
            display: "grid",
            gridTemplateColumns: getDeviceDetailsTemplate(columns),
            alignItems: "center",
            minHeight: ROW_MIN_HEIGHT,
            borderRadius: ROW_RADIUS,
            backgroundColor: (theme) => highlight
                ? alpha(theme.palette.primary.main, 0.08)
                : theme.palette.background.paper,
            // Inset ring instead of a border so the 1px stroke never shifts the column grid.
            boxShadow: (theme) => highlight
                ? "none"
                : `inset 0 0 0 1px ${alpha(theme.palette.grey[500], 0.24)}`,
        } },
        highlight && (React.createElement(Box, { component: "svg", "aria-hidden": true, sx: {
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                clipPath: `inset(0 round ${ROW_RADIUS})`,
            } },
            React.createElement(Box, { component: "rect", x: 0, y: 0, width: "100%", height: "100%", rx: 32, ry: 32, fill: "none", strokeWidth: 2, strokeDasharray: "4 4", sx: {
                    stroke: (theme) => alpha(theme.palette.primary.main, 0.2),
                } }))),
        columns.map((col) => {
            var _a;
            return (React.createElement(Box, { key: col.field, "data-field": col.field, sx: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: JUSTIFY[(_a = col.align) !== null && _a !== void 0 ? _a : "left"],
                    minWidth: 0,
                    px: CELL_PADDING_X,
                    // Content overflows its cell instead of wrapping (e.g. "In-active", dates).
                    whiteSpace: "nowrap",
                    "& > *": { flexShrink: 0 },
                } }, renderCell(col)));
        })));
};
/** Icon + value pair used for the CO2 / temperature / humidity readings (Figma: chip-label 13/18). */
export const ReadingValue = ({ icon, label, tooltip, width, outOfRange, }) => (React.createElement(DesignTooltip, { title: tooltip },
    React.createElement(Box, { sx: {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: width ? "flex-start" : undefined,
            width,
            gap: "4px",
            whiteSpace: "nowrap",
        } },
        icon,
        React.createElement(Typography, { component: "span", sx: {
                fontSize: 13,
                lineHeight: "18px",
                fontWeight: outOfRange ? 600 : 500,
                color: outOfRange ? TEMPERATURE_STATUS_COLORS[outOfRange].text : "text.primary",
            } }, label))));
/** Soft pill showing the node's temperature status (Figma: Chip, Variant=Soft, Size=S). */
export const TemperatureStatusChip = ({ status, size = "small", reason, }) => {
    const { t } = useTranslation("common");
    const inactive = status === "inactive";
    const label = inactive
        ? "-"
        : t(`common:labels.temperature${status.charAt(0).toUpperCase()}${status.slice(1)}`);
    const colors = TEMPERATURE_STATUS_COLORS[status];
    const chip = (React.createElement(Box, { sx: {
            display: "inline-flex",
            alignItems: "center",
            height: size === "medium" ? 32 : 24,
            px: size === "medium" ? "12px" : "8px",
            borderRadius: "32px",
            fontSize: 13,
            lineHeight: "18px",
            fontWeight: 500,
            whiteSpace: "nowrap",
            backgroundColor: (theme) => inactive ? alpha(theme.palette.grey[500], 0.08) : colors.bg,
            color: (theme) => inactive ? theme.palette.text.primary : colors.text,
        } }, label));
    return reason ? (React.createElement(DesignTooltip, { title: React.createElement("span", { style: { whiteSpace: "pre-line" } }, reason) }, chip)) : (chip);
};
/**
 * Active / In-active marker. The active dot is padded to the 16px slot the design gives it
 * (12px dot + 2px each side) so the label lines up with the rest of the status column.
 */
export const DeviceStatusIndicator = ({ active, }) => (React.createElement(StatusIndicator, { variant: active ? "active" : "inactive", sx: active ? { "& > svg": { mx: "2px" } } : undefined }));
